import axios, { InternalAxiosRequestConfig, AxiosInstance } from "axios";
import {
  buildRequestIntegrityHeaders,
  shouldAttachRequestIntegrity,
} from "@/utils/requestIntegrity";

const isDev = process.env.NODE_ENV !== "production";

/* ------------------------------------------------------------------ */
/* 🌟 Segédfüggvény – van‑e token?                                     */
/* ------------------------------------------------------------------ */
const hasAccessToken = () => !!localStorage.getItem("accessToken");

let accessToken: string | null = localStorage.getItem("accessToken");
let isRefreshing = false;
type RefreshSubscriber = {
  resolve: (token: string) => void;
  reject: (error: any) => void;
};
let subscribers: RefreshSubscriber[] = [];

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function setAuthHeader(config: InternalAxiosRequestConfig, token: string) {
  (config.headers as any) = config.headers || {};
  (config.headers as any).Authorization = `Bearer ${token}`;
}

function debugAuthLog(message: string, extra?: unknown) {
  if (!isDev) return;
  if (extra !== undefined) {
    console.debug(`[auth] ${message}`, extra);
    return;
  }
  console.debug(`[auth] ${message}`);
}

/* ------------------------------------------------------------------ */
/* 🚀 Token‑frissítéshez szükséges helper‑ek                            */
/* ------------------------------------------------------------------ */
function onRefreshed(token: string) {
  subscribers.forEach(({ resolve }) => resolve(token));
  subscribers = [];
}

function onRefreshFailed(error: any) {
  subscribers.forEach(({ reject }) => reject(error));
  subscribers = [];
}

function addRefreshSubscriber(
  resolve: (token: string) => void,
  reject: (error: any) => void = () => undefined
) {
  subscribers.push({ resolve, reject });
}

/* ------------------------------------------------------------------ */
/* 🔧 Axios instance                                                   */
/* ------------------------------------------------------------------ */

const api: AxiosInstance = axios.create({
  baseURL:
    process.env.VUE_APP_BACKEND_API_URL &&
    process.env.VUE_APP_BACKEND_API_URL.trim() !== ""
      ? process.env.VUE_APP_BACKEND_API_URL
      : "https://api.vskweb.hu",
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

/* ------------------------------------------------------------------ */
/* 📝 Helper a token beállításához                                     */
/* ------------------------------------------------------------------ */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) {
    localStorage.setItem("accessToken", token);
  } else {
    localStorage.removeItem("accessToken");
  }
}

/* ------------------------------------------------------------------ */
/* 📤 REQUEST interceptor – token csatolás                              */
/* ------------------------------------------------------------------ */
api.interceptors.request.use(
  async (config) => {
    // Login / refresh-token végpontokra nem rakunk Authorization-t
    if (
      config.url?.includes("/user/login") ||
      config.url?.includes("/user/refresh-token")
    ) {
      return config;
    }

    // Ha nincs token, hagyjuk a kérést menni; sem várakozás, sem header
    if (!hasAccessToken()) {
      return config;
    }

    // Ha épp frissítünk, várjuk meg az új tokent
    if (isRefreshing) {
      debugAuthLog("Request waits for token refresh", {
        url: config.url,
      });
      return new Promise((resolve, reject) =>
        addRefreshSubscriber(
          (token: string) => {
            setAuthHeader(config, token);
            resolve(config);
          },
          (error: any) => reject(error)
        )
      );
    }

    // Van token → csatoljuk
    setAuthHeader(config, String(accessToken || ""));
    return config;
  },
  (error) => Promise.reject(error)
);

/* ------------------------------------------------------------------ */
/* ♻️ RESPONSE interceptor – 401 esetén refresh, de csak tokennel      */
/* ------------------------------------------------------------------ */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as CustomAxiosRequestConfig;

    // Nem 401, vagy már retry volt → dobjuk tovább
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // 🔒 NINCS token → nincs értelme frissíteni, dobjuk a hibát
    if (!hasAccessToken()) {
      return Promise.reject(error);
    }

    /* -------------------------------------------------------------- */
    /* Van token → próbálunk frissíteni                               */
    /* -------------------------------------------------------------- */
    original._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;
      let refreshedToken: string | null = null;
      debugAuthLog("Starting token refresh");

      try {
        const { data } = await axios.post(
          (process.env.VUE_APP_BACKEND_API_URL || "https://api.vskweb.hu") +
            "/user/refresh-token",
          {},
          { withCredentials: true }
        );

        if (data.accessToken) {
          setAccessToken(data.accessToken);
          refreshedToken = data.accessToken;
          onRefreshed(data.accessToken);
          debugAuthLog("Token refresh success");
        } else {
          const refreshError = new Error(
            "Token frissítés sikertelen – nincs új token"
          );
          setAccessToken(null);
          onRefreshFailed(refreshError);
          debugAuthLog("Token refresh failed: no token in response");
          return Promise.reject(refreshError);
        }
      } catch (err) {
        setAccessToken(null);
        onRefreshFailed(err);
        debugAuthLog("Token refresh failed with error", err);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }

      if (refreshedToken) {
        debugAuthLog("Retrying original request after refresh", {
          url: original.url,
        });
        setAuthHeader(original, refreshedToken);
        return api(original);
      }
    }

    /* ––Várjuk, amíg a fenti refresh befejeződik–– */
    debugAuthLog("Queueing request until refresh completes", {
      url: original.url,
    });
    return new Promise((resolve, reject) => {
      addRefreshSubscriber(
        (newToken: string) => {
          debugAuthLog("Replaying queued request", { url: original.url });
          setAuthHeader(original, newToken);
          resolve(api(original));
        },
        (refreshError: any) => reject(refreshError)
      );
    });
  }
);

export default api;
export { isRefreshing, addRefreshSubscriber };
